// Audio visualization constants
export const WAVEFORM_BAR_COUNT = 50;

// Formats seconds into MM:SS format
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const generateFallbackWaveform = (barCount: number): number[] => {
  return Array.from(
    { length: barCount },
    (_, i) => Math.sin(i * 0.3) * 12 + 16,
  );
};

export interface WaveformResult {
  waveformData: number[];
  duration: number;
}

export const generateWaveformData = async (
  audioBlob: Blob,
  barCount: number = WAVEFORM_BAR_COUNT,
): Promise<WaveformResult> => {
  try {
    const audioContext = new AudioContext();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const channelData = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.floor(channelData.length / barCount);
    const waveformData: number[] = [];

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = start + samplesPerBar;
      let sum = 0;

      for (let j = start; j < end; j++) {
        sum += Math.abs(channelData[j]);
      }

      const average = sum / samplesPerBar;
      const normalizedHeight = Math.max(8, Math.min(28, average * 100 + 8));
      waveformData.push(normalizedHeight);
    }

    audioContext.close();
    return { waveformData, duration: audioBuffer.duration };
  } catch (error) {
    console.error("Error generating waveform data:", error);
    return { waveformData: generateFallbackWaveform(barCount), duration: 0 };
  }
};

export const generateWaveformFromUrl = async (
  audioUrl: string,
  barCount: number = WAVEFORM_BAR_COUNT,
): Promise<WaveformResult> => {
  try {
    const response = await fetch(audioUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${response.statusText}`);
    }
    const audioBlob = await response.blob();
    return generateWaveformData(audioBlob, barCount);
  } catch (error) {
    console.error("Error generating waveform from URL:", error);
    return { waveformData: generateFallbackWaveform(barCount), duration: 0 };
  }
};
